import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Label,
  Loader,
  Grid,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

const SEVERITY_COLORS = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
  info: 'grey',
};

const STATUS_COLORS = {
  open: 'red',
  investigating: 'orange',
  dispositioned: 'yellow',
  resolving: 'teal',
  verification: 'blue',
  deferred: 'grey',
  closed: 'green',
};

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString();
}

export default function IssueDetail() {
  const { id } = useParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@qms/issues/${encodeURIComponent(id)}`)
      .then((json) => {
        setIssue(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading issue..." />
      </Container>
    );
  }
  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }
  if (!issue) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment>Issue not found.</Segment>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title={`${issue.report_id} — ${issue.title}`} />
      <Link to="/qms/issues">&larr; Issue Tracker</Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        {issue.report_id}
        <Header.Subheader>{issue.title}</Header.Subheader>
      </Header>

      {/* Severity + Status labels */}
      <div style={{ marginBottom: '1.5em' }}>
        <Label color={SEVERITY_COLORS[issue.severity] || 'grey'} size="large">
          {issue.severity_label}
        </Label>
        <Label color={STATUS_COLORS[issue.status] || 'grey'} size="large">
          {issue.status_label}
        </Label>
        <Label basic size="large">
          {issue.type_label}
        </Label>
        {issue.scope === 'systemic' && (
          <Label color="purple" size="large">
            Systemic
          </Label>
        )}
      </div>

      {/* Metadata grid */}
      <Segment>
        <Grid columns={2} stackable>
          <Grid.Row>
            <Grid.Column>
              <Table definition compact size="small">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={6}>Report ID</Table.Cell>
                    <Table.Cell><strong>{issue.report_id}</strong></Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Reported By</Table.Cell>
                    <Table.Cell>{issue.reported_by}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Reported At</Table.Cell>
                    <Table.Cell>{formatDate(issue.reported_at)}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Source</Table.Cell>
                    <Table.Cell>
                      {issue.source_type}
                      {issue.source_reference && (
                        <> ({issue.source_reference})</>
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Scope</Table.Cell>
                    <Table.Cell>{issue.scope}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Grid.Column>
            <Grid.Column>
              <Table definition compact size="small">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={6}>Component</Table.Cell>
                    <Table.Cell>{issue.affected_component || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Related DR</Table.Cell>
                    <Table.Cell>
                      {issue.related_dr_id ? (
                        <Link to={`/qms/register/${encodeURIComponent(issue.related_dr_id)}`}>
                          {issue.related_dr_id}
                        </Link>
                      ) : '—'}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Related CAPA</Table.Cell>
                    <Table.Cell>{issue.related_capa_id || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Related CR</Table.Cell>
                    <Table.Cell>{issue.related_cr_id || '—'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Last Updated</Table.Cell>
                    <Table.Cell>{formatDate(issue.updated_at)}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>

      {/* Description */}
      <Header as="h3">Description</Header>
      <Segment>
        <p style={{ whiteSpace: 'pre-wrap' }}>{issue.description}</p>
      </Segment>

      {/* Investigation */}
      {(issue.investigation_notes || issue.root_cause || issue.safety_impact) && (
        <>
          <Header as="h3">
            <Icon name="search" /> Investigation
          </Header>
          <Segment>
            {issue.investigation_notes && (
              <>
                <Header as="h5">Investigation Notes</Header>
                <p style={{ whiteSpace: 'pre-wrap' }}>{issue.investigation_notes}</p>
              </>
            )}
            {issue.root_cause && (
              <>
                <Header as="h5">Root Cause</Header>
                <p style={{ whiteSpace: 'pre-wrap' }}>{issue.root_cause}</p>
              </>
            )}
            {issue.safety_impact && (
              <>
                <Header as="h5">Safety Impact</Header>
                <p style={{ whiteSpace: 'pre-wrap' }}>{issue.safety_impact}</p>
              </>
            )}
          </Segment>
        </>
      )}

      {/* Disposition */}
      {issue.disposition && (
        <>
          <Header as="h3">
            <Icon name="checkmark box" /> Disposition
          </Header>
          <Segment>
            <Table definition compact size="small">
              <Table.Body>
                <Table.Row>
                  <Table.Cell width={4}>Disposition</Table.Cell>
                  <Table.Cell>
                    <Label>{issue.disposition}</Label>
                  </Table.Cell>
                </Table.Row>
                {issue.disposition_evidence && (
                  <Table.Row>
                    <Table.Cell>Evidence</Table.Cell>
                    <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>
                      {issue.disposition_evidence}
                    </Table.Cell>
                  </Table.Row>
                )}
                <Table.Row>
                  <Table.Cell>Dispositioned By</Table.Cell>
                  <Table.Cell>{issue.disposition_by || '—'}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Dispositioned At</Table.Cell>
                  <Table.Cell>{formatDate(issue.disposition_at)}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Segment>
        </>
      )}

      {/* Resolution */}
      {(issue.resolution_notes || issue.verified_by) && (
        <>
          <Header as="h3">
            <Icon name="shield" /> Resolution & Verification
          </Header>
          <Segment>
            {issue.resolution_notes && (
              <>
                <Header as="h5">Resolution Notes</Header>
                <p style={{ whiteSpace: 'pre-wrap' }}>{issue.resolution_notes}</p>
              </>
            )}
            {issue.verified_by && (
              <Table definition compact size="small">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={4}>Verified By</Table.Cell>
                    <Table.Cell>{issue.verified_by}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Verified At</Table.Cell>
                    <Table.Cell>{formatDate(issue.verified_at)}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            )}
          </Segment>
        </>
      )}

      {/* Status timeline */}
      <Header as="h3">
        <Icon name="clock" /> Timeline
      </Header>
      <Segment>
        <Table definition compact size="small">
          <Table.Body>
            <Table.Row>
              <Table.Cell width={4}>Created</Table.Cell>
              <Table.Cell>{formatDate(issue.created_at)}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Status Changed</Table.Cell>
              <Table.Cell>
                {formatDate(issue.status_changed_at)}
                {issue.status_changed_by && ` by ${issue.status_changed_by}`}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Last Updated</Table.Cell>
              <Table.Cell>{formatDate(issue.updated_at)}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Segment>
    </Container>
  );
}
